import useFetchAndTrace, { FetchAndTraceErrorHandler } from "./fetchWithTrace";
import { useCallback, useState } from "react";
import settings from "../../constants/settings";

export interface DownloadableFileResponse {
  file_name: string;
  s3_key: string;
  link: string;
}

export interface S3FileUploadData {
  s3_bucket: string;
  s3_key: string;
  url: string;
  file_name: string;
  fields: { [id: string]: string };
}

export function useUploadFileToS3(errorHandler: FetchAndTraceErrorHandler) {
  const fetchAndTrace = useFetchAndTrace();

  const simpleSuccessHandler = (response: Response) => {
    if (!response.ok) {
      throw Error("Failed to upload file to s3");
    }
  };

  return useCallback(
    async (fileData: S3FileUploadData, file: Blob) => {
      const formData = new FormData();
      Object.keys(fileData.fields).forEach((key) => {
        formData.append(key, fileData.fields[key]!);
      });
      formData.append("file", file);
      formData.append("Content-Type", file.type);
      await fetchAndTrace(
        fileData.url,
        simpleSuccessHandler,
        errorHandler,
        {
          method: "POST",
          body: formData,
          contentType: null,
        },
        true,
      );
    },
    [errorHandler, fetchAndTrace],
  );
}

export interface UploadFilesResponse {
  upload_data: S3FileUploadData[];
}

export enum BoardUploadFileType {
  AUXILIARY = "Auxiliary",
  CUSTOMER_IMAGE = "Customer image",
}

export function useUploadBoardFiles(
  onError: () => void,
  fileType: BoardUploadFileType,
) {
  const fetchAndTrace = useFetchAndTrace();
  const uploadToS3 = useUploadFileToS3(onError);
  const [sending, setSending] = useState(false);

  const uploadBoardFiles = useCallback(
    (
      boardId: string,
      files: File[],
      onUpload: (fileLinks: UploadFilesResponse) => Promise<void> | void,
    ) => {
      setSending(true);

      const handleGetUploadLinks = async (response: Response) => {
        if (response.ok) {
          const fileLinks = (await response.json()) as UploadFilesResponse;
          await Promise.all(
            fileLinks.upload_data.map((fileUpload, fileIndex) =>
              uploadToS3(fileUpload, files[fileIndex]!),
            ),
          );
          setSending(false);
          void onUpload(fileLinks);
        } else {
          throw Error("Failed to get board file upload links");
        }
      };

      const uploadErrorHandler = () => {
        setSending(false);
        onError();
      };

      void fetchAndTrace(
        `${settings.api_url}/boards/${boardId}/files/${fileType}/upload`,
        handleGetUploadLinks,
        uploadErrorHandler,
        {
          body: JSON.stringify({
            file_names: files.map((file) => file.name),
          }),
          method: "POST",
        },
      );
    },
    [fetchAndTrace, onError, uploadToS3, fileType],
  );

  return { uploadBoardFiles, sending };
}

export function useUploadOrderFiles(onError: () => void) {
  const fetchAndTrace = useFetchAndTrace();
  const uploadToS3 = useUploadFileToS3(onError);
  const [sending, setSending] = useState(false);

  const uploadOrderFiles = useCallback(
    (
      orderId: string,
      files: File[],
      fileType: string | null,
      onUpload: (fileLinks: UploadFilesResponse) => Promise<void> | void,
    ) => {
      setSending(true);

      const handleGetUploadLinks = async (response: Response) => {
        if (response.ok) {
          const fileLinks = (await response.json()) as UploadFilesResponse;
          await Promise.all(
            fileLinks.upload_data.map((fileUpload, fileIndex) =>
              uploadToS3(fileUpload, files[fileIndex]!),
            ),
          );
          setSending(false);
          void onUpload(fileLinks);
        } else {
          throw Error("Failed to get order file upload links");
        }
      };

      const uploadErrorHandler = () => {
        setSending(false);
        onError();
      };

      void fetchAndTrace(
        `${settings.api_url}/orders/${orderId}/file-upload`,
        handleGetUploadLinks,
        uploadErrorHandler,
        {
          body: JSON.stringify({
            files: files.map((file) => ({
              file_name: file.name,
              file_type: fileType,
            })),
          }),
          method: "POST",
        },
      );
    },
    [fetchAndTrace, onError, uploadToS3],
  );

  return { uploadOrderFiles, sending };
}
